import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { AuthError } from '@/services/implementations/AuthService';
import { router, AppRoute } from '@/lib/router';
import { toast } from 'sonner';
import { SocialLoginSection } from './SocialLoginSection';
import { useTranslation } from '@/contexts/I18nContext';

interface LoginFormProps {
  onSwitchToRegister?: () => void;
  onSwitchToForgotPassword?: () => void;
  className?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToRegister,
  onSwitchToForgotPassword,
  className,
  ...props
}) => {
  const { login, sendOtp } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mfaCode: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendMFACode = async () => {
    if (!formData.email) {
      toast.error(t('auth.pleaseEnterEmailFirst'));
      return;
    }

    setIsSendingCode(true);
    try {
      await sendOtp(formData.email, 1, 'mfa'); // isLogin=1, purpose='mfa'
      setIsCodeSent(true);
      toast.success(t('auth.mfaCodeSent'));
    } catch (error: any) {
      toast.error(error.message || t('auth.failedToSendCode'));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(formData.email, formData.password, undefined, formData.mfaCode || undefined);
      // 登录成功，跳转到主页
      router.navigate(AppRoute.HOME);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message === 'MFA_REQUIRED') {
          setRequiresMFA(true);
          toast.error(t('auth.mfaRequired'));
        } else {
          toast.error(err.message);
        }
      } else if (err instanceof AuthError) {
        switch (err.code) {
          case '451':
            setRequiresMFA(true);
            toast.error(t('auth.mfaRequired'));
            break;
          case '453':
            toast.error(t('auth.userDoesNotExist'));
            break;
          case 'invalid_credentials':
            toast.error(t('auth.invalidCredentials'));
            break;
          default:
            toast.error(err.message || t('auth.loginError'));
        }
      } else {
        toast.error(t('auth.loginError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // const handleSocialLogin = async (provider: 'apple' | 'google') => {
  //   try {
  //     // 这里应该集成实际的社交登录SDK
  //     // TODO: 例如：Auth0, Firebase Auth, 或者其他社交登录提供商
      
  //     // 模拟社交登录数据（实际应用中这些数据来自社交登录SDK）
  //     const mockAuth0User = {
  //       sub: `${provider}|mock_user_id_${Date.now()}`,
  //       email: `user@${provider}.com`,
  //       name: `${provider.charAt(0).toUpperCase() + provider.slice(1)} User`,
  //       nickname: `${provider}user`,
  //       picture: `https://example.com/${provider}_avatar.jpg`,
  //       email_verified: true
  //     };

  //     await socialLogin(mockAuth0User);
  //     router.navigate(AppRoute.HOME);
  //   } catch (error) {
  //     console.error(`${provider} login failed:`, error);
  //     toast.error(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login failed`);
  //   }
  // };

  const isFormValid = formData.email && formData.password && (!requiresMFA || formData.mfaCode);

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <img src="/icons/logo.png" alt="Logo" className="w-12 h-12 mb-4" />
                <h1 className="text-2xl font-bold">{t('auth.welcomeBack')}</h1>
                <p className="text-muted-foreground text-balance">
                  {t('auth.loginWithSocialOrEmail')}
                </p>
              </div>

              {/* 社交登录部分 */}
              <SocialLoginSection
                disabled={isLoading}
                dividerText={t('auth.orContinueWithEmail')}
              />

              {/* 邮箱输入 */}
              <div className="grid gap-3">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                />
              </div>

              {/* 密码输入 */}
              <div className="grid gap-3">
                <div className="flex items-center">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <button
                    type="button"
                    onClick={onSwitchToForgotPassword}
                    className="ml-auto text-sm underline-offset-2 hover:underline"
                  >
                    {t('auth.forgotPasswordLink')}
                  </button>
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                />
              </div>

              {/* MFA 验证码输入 */}
              {requiresMFA && (
                <div className="grid gap-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="mfaCode">{t('auth.verificationCodeMFA')}</Label>
                    {!isCodeSent && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSendMFACode}
                        disabled={isSendingCode || !formData.email}
                      >
                        {isSendingCode ? t('auth.sending') : t('auth.sendCode')}
                      </Button>
                    )}
                  </div>
                  <Input
                    id="mfaCode"
                    name="mfaCode"
                    type="text"
                    placeholder="123456"
                    value={formData.mfaCode}
                    onChange={handleInputChange}
                    maxLength={6}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    {isCodeSent
                      ? t('auth.codeSentTo', formData.email).replace('{email}', formData.email)
                      : t('auth.clickSendCodeDescription')
                    }
                  </p>
                  {isCodeSent && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSendMFACode}
                      disabled={isSendingCode}
                    >
                      {isSendingCode ? t('auth.sending') : t('auth.resendCode')}
                    </Button>
                  )}
                </div>
              )}

              {/* 登录按钮 */}
              <Button
                type="submit"
                className="w-full"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('auth.loggingIn')}
                  </div>
                ) : (
                  t('auth.login')
                )}
              </Button>

              {/* 注册链接 */}
              <div className="text-center text-sm">
                {t('auth.dontHaveAccount')}{" "}
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  className="underline underline-offset-4"
                >
                  {t('auth.signUp')}
                </button>
              </div>
            </div>
          </form>
          <div className="bg-muted relative hidden md:block">
            <img
              src="/imgs/login-cover.avif"
              alt="Login illustration"
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
            />
          </div>
        </CardContent>
      </Card>
      <div className="text-muted-foreground text-center text-xs text-balance">
        {t('auth.termsAndPrivacy')}{" "}
        <a href="#" className="underline underline-offset-4 hover:text-primary">
          {t('auth.termsOfService')}
        </a>{" "}
        {t('auth.and')}{" "}
        <a href="#" className="underline underline-offset-4 hover:text-primary">
          {t('auth.privacyPolicy')}
        </a>.
      </div>
    </div>
  );
}; 